import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, CreditCard, Loader2 } from 'lucide-react';
import { useStripe } from '@/hooks/use-stripe';
import { toast } from '@/hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PluginPurchaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pluginName: string;
  pluginSlug: string;
}

const licenseTiers = [
  { id: '1-site', label: '1 site license', sites: 1, price: 211 },
  { id: '5-sites', label: 'Up to 5 sites', sites: 5, price: 311 },
  { id: '10-sites', label: 'Up to 10 sites', sites: 10, price: 411 },
  { id: '25-sites', label: 'Up to 25 sites', sites: 25, price: 511 },
];

export const PluginPurchaseDialog = ({
  open,
  onOpenChange,
  pluginName,
  pluginSlug
}: PluginPurchaseDialogProps) => {
  const { createPayment, isLoading } = useStripe();
  const [selectedTier, setSelectedTier] = useState(licenseTiers[0].id);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  const currentTier = licenseTiers.find(tier => tier.id === selectedTier) || licenseTiers[0];

  const validateForm = () => {
    const newErrors: { name?: string; email?: string } = {};

    if (!customerName.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!customerEmail.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePurchase = async () => {
    console.log('🔵 [Purchase] Button clicked, validating form...');

    if (!validateForm()) {
      console.log('🔴 [Purchase] Form validation failed');
      return;
    }

    console.log('🟢 [Purchase] Form validated, preparing payment...');

    // Build success URL that returns to plugin page with success params
    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/wordpress-plugins/${pluginSlug}?success=true&session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${baseUrl}/wordpress-plugins/${pluginSlug}?canceled=true`;

    console.log('🔵 [Purchase] URLs:', { baseUrl, successUrl, cancelUrl });

    try {
      console.log('🔵 [Purchase] Calling createPayment...');
      const result = await createPayment({
        customerName: customerName.trim(),
        customerEmail: customerEmail.trim().toLowerCase(),
        customerPhone: '',
        serviceType: `WordPress Plugin - ${pluginName}`,
        serviceTier: `Premium - ${currentTier.label}`,
        price: currentTier.price,
        projectDescription: `${pluginName} Premium License - ${currentTier.label} - Annual subscription`,
        successUrl,
        cancelUrl,
      });

      console.log('🔵 [Purchase] createPayment result:', result);

      if (!result.success) {
        console.error('🔴 [Purchase] Payment failed:', result);
        toast({
          title: "Error",
          description: "Failed to initiate payment. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('🔴 [Purchase] Exception:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto mx-4">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl">Upgrade to Premium</DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            Unlock all premium features of {pluginName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Pricing Display */}
          <div className="text-center py-4 sm:py-6 bg-muted/30 rounded-lg">
            <div className="text-xs sm:text-sm text-muted-foreground mb-1">From just</div>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-base sm:text-lg align-top">$</span>
              <span className="text-4xl sm:text-5xl font-bold">{currentTier.price}</span>
              <span className="text-xs sm:text-sm text-muted-foreground">/ Per year</span>
            </div>
          </div>

          {/* License Tier Selector */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Select License</Label>
            <Select value={selectedTier} onValueChange={setSelectedTier}>
              <SelectTrigger className="w-full bg-primary text-primary-foreground border-primary text-sm sm:text-base">
                <SelectValue placeholder="Select license type" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                {licenseTiers.map((tier) => (
                  <SelectItem key={tier.id} value={tier.id} className="text-sm sm:text-base">
                    {tier.label} - ${tier.price}/year
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Customer Information */}
          <div className="space-y-4 pt-2 border-t">
            <div className="space-y-2">
              <Label htmlFor="customerName" className="text-sm font-medium">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="customerName"
                type="text"
                placeholder="John Doe"
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                  if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
                }}
                className={errors.name ? 'border-destructive' : ''}
              />
              {errors.name && (
                <p className="text-xs text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="customerEmail" className="text-sm font-medium">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="customerEmail"
                type="email"
                placeholder="john@example.com"
                value={customerEmail}
                onChange={(e) => {
                  setCustomerEmail(e.target.value);
                  if (errors.email) setErrors(prev => ({ ...prev, email: undefined }));
                }}
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Your license key will be sent to this email
              </p>
            </div>
          </div>

          <Button
            onClick={handlePurchase}
            disabled={isLoading}
            className="w-full text-sm sm:text-base gap-2"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                BUY NOW - ${currentTier.price}
              </>
            )}
          </Button>

          {/* Premium Features */}
          <div className="bg-muted/30 rounded-lg p-3 sm:p-4 space-y-2">
            <h4 className="font-semibold text-center text-primary mb-2 sm:mb-3 text-sm sm:text-base">INCLUDES</h4>
            <div className="space-y-1.5 sm:space-y-2">
              {[
                'Unlimited activity retention',
                'Full 450+ activity logging',
                'AI-powered anomaly detection',
                'Real-time dashboard',
                'Complete undo system',
                'Priority support',
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-2 justify-center border-b border-dashed border-muted-foreground/20 pb-1.5 sm:pb-2">
                  <Check className="w-3 h-3 text-primary" />
                  <span className="text-xs sm:text-sm text-center">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[10px] sm:text-xs text-center text-muted-foreground">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
